import { useEffect, useState } from 'react';

/**
 * The useDebounce hook is useful for delaying the execution of functions or state updates
 * until a specified time period has passed without any further changes to the input value.
 * @param {T} value - The value that will change later (e.g. a state).
 * @param {undefined | number} delay - The delay time in millisecond (default value: 500 ms).
 * @param {T} initialValue - The optional initial value before any debounce is applied.
 * @return {T} debouncedValue - The debounced value.
 */
export const useDebounce = <T,>(
	value: T,
	delay: number = 500,
	initialValue: T = value
): T => {
	const [debouncedValue, setDebouncedValue] = useState<T>(initialValue);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedValue(value), delay);

		return () => {
			clearTimeout(timer);
		};
	}, [value, delay]);

	return debouncedValue;
};

/**
 * Same as the previous one, the only difference is that the debounce on the value only happens if the given condition is true.
 * @param {T} value - The value that will change later (e.g. a state).
 * @param {boolean} condition - The condition, if it is true, the time delay (debounce)will occur.
 * @param {undefined | number} delay - The delay time in millisecond (default value: 500 ms).
 * @return {T} debouncedValue - The debounced value.
 */
export const useConditionalDebounce = <T,>(
	value: T,
	condition: boolean,
	delay?: number
): T => {
	const [debouncedValue, setDebouncedValue] = useState<T>(value);

	useEffect(() => {
		let timer: NodeJS.Timeout | null = null;

		if (condition) {
			timer = setTimeout(() => setDebouncedValue(value), delay || 500);
		} else {
			setDebouncedValue(value);
		}

		return () => {
			if (timer) {
				clearTimeout(timer);
			}
		};
	}, [value, delay]);

	return debouncedValue;
};
